import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '../../../lib/errors';
import { baoCaoDoanhNghiepRepository as repo } from '../../../lib/repositories/bao-cao-doanh-nghiep.repository';
import type { CreateFolderInput, Folder, UpdateFolderInput } from '../types';
import { KHOA_BAO_CAO, lamMoiTaiLieu, thuLai } from './useBaoCao';
import { THAM_SO_THU_MUC } from './useNguon';

/** Giá trị đặc biệt của `?thuMuc=` cho mục "Chưa phân loại". */
export const CHUA_PHAN_LOAI = 'chua-phan-loai';

/** Cây thư mục của một nguồn, kèm số tài liệu từng thư mục. Khoá cache mang theo nguồn. */
export function useDanhSachThuMuc(nguon: string | undefined, bat: boolean) {
  return useQuery<Folder[], ApiError>({
    queryKey: KHOA_BAO_CAO.thuMucCua(nguon),
    queryFn: () => repo.thuMuc(nguon, true),
    enabled: bat,
    staleTime: 30_000,
    retry: thuLai,
  });
}

export function useDemChuaPhanLoai(nguon: string | undefined, bat: boolean) {
  return useQuery<{ documentsTotal: number }, ApiError>({
    queryKey: KHOA_BAO_CAO.chuaPhanLoai(nguon),
    queryFn: () => repo.demChuaPhanLoai(nguon),
    enabled: bat,
    staleTime: 30_000,
    retry: thuLai,
  });
}

/**
 * Thư mục đang xem, giữ trên URL (`?thuMuc=<id>`). Ba trạng thái khác nhau:
 * - `undefined`: "Tất cả" — không lọc, đúng thứ app di động thấy;
 * - `CHUA_PHAN_LOAI`: chỉ tài liệu ở gốc;
 * - `<id>`: đúng thư mục ấy.
 *
 * Id trên URL chỉ dùng khi có thật trong cây của nguồn hiện tại (thư mục đã xoá, id của nguồn khác còn sót
 * lại sau khi đổi nguồn) — không thì rơi về "Tất cả", trạng thái luôn hợp lệ. Cây chưa tải xong thì cũng
 * coi là "Tất cả" để không gửi một id chưa kiểm xuống API.
 */
export function useThuMucDangXem(danhSach: readonly Folder[]): {
  chon: string | undefined;
  datChon: (giaTri: string | undefined) => void;
} {
  const [thamSo, datThamSo] = useSearchParams();
  const tuUrl = thamSo.get(THAM_SO_THU_MUC) ?? undefined;
  const chon =
    tuUrl === CHUA_PHAN_LOAI
      ? CHUA_PHAN_LOAI
      : tuUrl !== undefined && danhSach.some((f) => f.id === tuUrl)
        ? tuUrl
        : undefined;

  const datChon = useCallback(
    (giaTri: string | undefined) => {
      datThamSo(
        (cu) => {
          const moi = new URLSearchParams(cu);
          if (giaTri === undefined) moi.delete(THAM_SO_THU_MUC);
          else moi.set(THAM_SO_THU_MUC, giaTri);
          return moi;
        },
        { replace: true },
      );
    },
    [datThamSo],
  );

  return { chon, datChon };
}

export function useTaoThuMuc() {
  const qc = useQueryClient();
  return useMutation<Folder, ApiError, CreateFolderInput>({
    mutationFn: (du) => repo.taoThuMuc(du),
    onSuccess: () => lamMoiTaiLieu(qc),
  });
}

export function useSuaThuMuc() {
  const qc = useQueryClient();
  return useMutation<Folder, ApiError, { id: string; du: UpdateFolderInput }>({
    mutationFn: ({ id, du }) => repo.suaThuMuc(id, du),
    onSuccess: () => lamMoiTaiLieu(qc),
  });
}

export function useChuyenThuMuc() {
  const qc = useQueryClient();
  return useMutation<Folder, ApiError, { id: string; parentId: string | null }>({
    mutationFn: ({ id, parentId }) => repo.chuyenThuMuc(id, parentId),
    onSuccess: () => lamMoiTaiLieu(qc),
  });
}

export function useXoaThuMuc() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, ApiError, string>({
    mutationFn: (id) => repo.xoaThuMuc(id),
    onSuccess: () => lamMoiTaiLieu(qc),
  });
}
